import {
  ArrowGo,
  ArrowSelect,
  Cel,
  Check,
  Comment,
  EmoClock,
  Heart,
  HeartFilled,
  Market,
  Pin,
  Share,
  StarFilled,
  Today,
  Truck,
  UserFilled,
  Write,
  Www,
} from "@assets"
import { Head, Link, router } from "@inertiajs/react"
import { ChevronLeft, ChevronRight, Pencil, Star, Trash } from "lucide-react"
import type React from "react"
import { useState } from "react"

import { AuthButton } from "@/components/auth-button"
import { ContentActionsMenu } from "@/components/content-actions-menu"
import FeedItem, {
  type FeedItem as FeedItemData,
} from "@/components/feed/feed-item"
import { PageHeader } from "@/components/page-header"
import { RichTextEditor } from "@/components/rich-text-editor"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAuth } from "@/hooks/use-auth"
import { useFavorites } from "@/hooks/use-favorites"
import AppLayout from "@/layouts/app-layout"
import { showChallengeToast, showPointsToast } from "@/lib/notifications"
import { cn } from "@/lib/utils"
import {
  businessPath,
  businessesEditPath,
  chatGroupsPath,
  marketplacePath,
  userPath,
} from "@/routes"

interface Business {
  id: number
  business_name: string
  business_type: string
  description: string
  address: string
  phone?: string
  email?: string
  website?: string
  is_verified: boolean
  rating?: number
  total_reviews?: number
  subscription_tier: string
  location?: string
  neighborhood: {
    id: number
    name: string
    slug: string
  }
  city: {
    id: number
    name: string
    code: string
  }
  user: {
    id: number
    name: string
    email: string
  }
  owner: {
    id: number
    name: string
    email: string
    avatar_url?: string
    verified?: boolean
  }
  images?: {
    url: string
  }[]
  hours: Record<
    string,
    {
      open: string
      close: string
      closed: boolean
    }
  >
  features: string[]
  has_delivery: boolean
  has_pickup: boolean
  delivery_zones: string[]
  created_at: string
  is_favorited?: boolean
  favorites_count?: number
}

interface Review {
  id: number
  rating: number
  comment: string
  created_at: string
  user: {
    id: number
    name: string
    avatar_url?: string
    verified?: boolean
  }
  images?: {
    id: number
    url: string
  }[]
}

interface Product {
  id: number
  name: string
  description?: string
  price: number
  image_url?: string
  category: { id: number; name: string }
  business: { id: number; name: string; verified?: boolean }
  images?: { id: number; url: string }[]
  available: boolean
  in_stock?: boolean
  stock_quantity?: number
  condition?: string
  created_at?: string
}

interface Props {
  business: Business
  reviews: Review[]
  products: Product[]
  can_edit?: boolean
  can_review?: boolean
  pagination?: {
    current_page: number
    per_page: number
    total_count: number
    total_pages: number
  }
  reviews_pagination?: {
    current_page: number
    per_page: number
    total_count: number
    total_pages: number
  }
  similar_businesses?: {
    id: number
    name: string
    rating: number
    price_range: string
    image_url?: string
  }[]
}

export default function BusinessShow({
  business,
  reviews,
  products,
  pagination,
  reviews_pagination,
  can_edit,
  can_review,
}: Props) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [rating, setRating] = useState<number>(0)
  const [comment, setComment] = useState<string>("")
  // Force remount of review form inputs (RichTextEditor) after successful submit
  const [formResetKey, setFormResetKey] = useState<number>(0)
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)
  const { user } = useAuth()
  const isAdmin = !!user?.is_admin
  const isOwner = !!user && !!business?.owner && user.id === business.owner.id
  const [reviewsState, setReviewsState] = useState<Review[]>(reviews)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Determine if user has already reviewed: prefer backend-provided can_review flag
  const alreadyReviewed =
    can_review === false ||
    (!!user && reviewsState.some((r) => r.user.id === user.id))
  const [editingReviewId, setEditingReviewId] = useState<number | null>(null)
  const [editRating, setEditRating] = useState<number>(0)
  const [editComment, setEditComment] = useState<string>("")

  // CSRF helper
  const getCsrf = () =>
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") ?? ""

  const {
    isFavorited: getStoreFavorite,
    setFavorite: setStoreFavorite,
    getFavoritesCount: getStoreCount,
    setFavoritesCount: setStoreCount,
  } = useFavorites()

  // Use store value if available, otherwise fallback to props
  const storedFavorite = getStoreFavorite("business", business.id)
  const storedCount = getStoreCount("business", business.id)
  const actualFavorited = storedFavorite ?? business.is_favorited
  const actualFavoritesCount = storedCount ?? business.favorites_count ?? 0

  const handleFavorite = () => {
    const newValue = !actualFavorited
    const newCount = newValue
      ? actualFavoritesCount + 1
      : Math.max(0, actualFavoritesCount - 1)

    // Sync with global store immediately for optimistic update
    setStoreFavorite("business", business.id, newValue)
    setStoreCount("business", business.id, newCount)

    router.post(
      `${businessPath(business.id)}/favorite`,
      {},
      {
        preserveScroll: true,
        onError: () => {
          // Revert on error
          setStoreFavorite("business", business.id, !newValue)
          setStoreCount(
            "business",
            business.id,
            newValue ? Math.max(0, newCount - 1) : newCount + 1,
          )
        },
      },
    )
  }

  const handleContact = () => {
    router.post(
      chatGroupsPath(),
      { business_id: business.id },
      {
        preserveScroll: true,
      },
    )
  }

  const handleShare = async () => {
    try {
      const url = window.location.href
      const title = business?.business_name ?? "Negocio"
      if (navigator.share) {
        await navigator.share({ title, url })
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url)
      }
    } catch {
      // no-op
    }
  }

  const handleDelete = () => {
    if (!confirm("¿Eliminar este negocio? Esta acción no se puede deshacer."))
      return
    router.delete(businessPath(business.id), {
      preserveScroll: false,
      onSuccess: () => {
        router.visit(marketplacePath())
      },
    })
  }

  // Inline edit helpers for user's own review
  const beginEditReview = (review: Review) => {
    setEditingReviewId(review.id)
    setEditRating(review.rating)
    setEditComment(review.comment || "")
  }

  const cancelEditReview = () => {
    setEditingReviewId(null)
    setEditRating(0)
    setEditComment("")
  }

  const submitEditReview = (
    e: React.FormEvent<HTMLFormElement>,
    review: Review,
  ) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    formData.set("review[rating]", String(editRating))
    formData.set("review[comment]", editComment)
    formData.set("_method", "patch")
    router.post(`/reseñas/${review.id}`, formData, {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => {
        cancelEditReview()
      },
    })
  }

  const deleteReview = (review: Review) => {
    if (!confirm("¿Seguro que deseas eliminar esta reseña?")) return
    router.delete(`/reseñas/${review.id}`, {
      preserveScroll: true,
    })
  }

  const handleReview = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formEl = e.currentTarget
    // Guard: prevent owners from reviewing their own business
    if (isOwner || isSubmitting) return
    // Ensure a rating is provided
    if (!rating || rating < 1 || rating > 5) return

    setIsSubmitting(true)
    const csrf = getCsrf()
    const formData = new FormData(formEl)
    // Ensure nested params as reviews_controller expects: params.require(:review)
    formData.delete("comment")
    formData.delete("rating")
    formData.set("review[rating]", String(rating))
    formData.set("review[comment]", comment)
    formData.set("business_id", String(business.id))

    fetch(`/reseñas`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrf,
      },
      body: formData,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(await res.text())
        const data = (await res.json()) as {
          message: string
          review: Review
          points_awarded?: { points: number; reason?: string }
          challenge_completed?: {
            title: string
            description: string
            points: number
            icon?: string
          }
        }
        // Add new review to list
        setReviewsState((prev) => [data.review, ...prev])
        // Reset form
        formEl.reset()
        setRating(0)
        setComment("")
        setFormResetKey((k) => k + 1)
        setIsReviewModalOpen(false)
        // Show toasts
        if (data.points_awarded) {
          showPointsToast(data.points_awarded)
        }
        if (data.challenge_completed) {
          showChallengeToast(data.challenge_completed)
        }
      })
      .catch((err) => {
        console.error("[Review] Error creating review:", err)
      })
      .finally(() => setIsSubmitting(false))
  }

  const SHORT_MONTHS = [
    "ene",
    "feb",
    "mar",
    "abr",
    "may",
    "jun",
    "jul",
    "ago",
    "sep",
    "oct",
    "nov",
    "dic",
  ]

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return dateString

    const day = date.getDate()
    const month = SHORT_MONTHS[date.getMonth()]
    const year = date.getFullYear()
    const currentYear = new Date().getFullYear()

    return `${day} ${month}${year === currentYear ? "" : ` ${year}`}`
  }

  const getCurrentDay = () => {
    const days = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ]
    return days[new Date().getDay()]
  }

  const isOpenNow = () => {
    if (!business.hours) return null
    const currentDay = getCurrentDay()
    const dayHours = business.hours[currentDay]

    if (!dayHours || dayHours.closed) return false

    const now = new Date()
    const currentTime = now.getHours() * 100 + now.getMinutes()
    const openTime = parseInt(dayHours.open.replace(":", ""))
    const closeTime = parseInt(dayHours.close.replace(":", ""))

    // Handle hours that cross midnight (e.g., 12:00 PM - 02:00 AM)
    if (closeTime < openTime) {
      // If current time is after opening OR before closing (next day)
      return currentTime >= openTime || currentTime <= closeTime
    }

    // Normal case: opening and closing on same day
    return currentTime >= openTime && currentTime <= closeTime
  }

  const nextImage = () => {
    if (business.images && business.images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % business.images!.length)
    }
  }

  const prevImage = () => {
    if (business.images && business.images.length > 0) {
      setCurrentImageIndex(
        (prev) =>
          (prev - 1 + business.images!.length) % business.images!.length,
      )
    }
  }

  const openNow = isOpenNow()

  const renderReviewCTA = () => {
    if (isOwner) {
      return (
        <div className="text-muted-foreground text-sm">
          No podés dejar una reseña en tu propio negocio.
        </div>
      )
    }

    if (alreadyReviewed) {
      return (
        <div className="text-muted-foreground text-sm">
          Ya has dejado una reseña para este negocio. Puedes editarla.
        </div>
      )
    }

    return (
      <AuthButton
        className="h-[49px] w-full rounded-[14px] border-0 text-[15px] font-medium tracking-[0.45px]"
        onClick={() => setIsReviewModalOpen(true)}
        authAction="register"
      >
        <Write className="size-6 fill-white" />
        Escribir Reseña
      </AuthButton>
    )
  }

  const PaginationControls = ({
    currentPage,
    totalPages,
    onPrev,
    onNext,
    disablePrev,
    disableNext,
  }: {
    currentPage: number
    totalPages: number
    onPrev: () => void
    onNext: () => void
    disablePrev: boolean
    disableNext: boolean
  }) => (
    <div className="flex h-[34px] w-full items-center gap-2 rounded-[8px] bg-white/50">
      <Button
        className="text-primary hover:bg-primary/10 bg-card h-[34px] w-[34px] rounded-[8px] border-0 p-0 shadow-[0px_0px_4px_rgba(0,0,0,0.16)]"
        variant="ghost"
        size="sm"
        disabled={disablePrev}
        onClick={onPrev}
      >
        <ArrowSelect className="fill-primary size-6 rotate-90" />
      </Button>
      <span className="text-muted-foreground flex-1 text-center text-[13px] tracking-[0.2px]">
        Página {currentPage} de {totalPages}
      </span>
      <Button
        className="text-primary hover:bg-primary/10 bg-card h-[34px] w-[34px] rounded-[8px] border-0 p-0 shadow-[0px_0px_4px_rgba(0,0,0,0.16)]"
        variant="ghost"
        size="sm"
        disabled={disableNext}
        onClick={onNext}
      >
        <ArrowSelect className="fill-primary size-6 rotate-270" />
      </Button>
    </div>
  )

  return (
    <AppLayout
      showEditButton={can_edit}
      showDeleteButton={can_edit}
      showBackButton={false}
      showShareButton={false}
      showFavoriteButton={false}
      onEdit={() => router.visit(businessesEditPath(business.id))}
      onDelete={handleDelete}
    >
      <Head title={business?.business_name ?? "Negocio"} />

      <div className="mx-0 max-w-6xl sm:mx-auto md:mx-auto md:space-y-6 md:p-6 lg:mx-auto xl:mx-auto">
        {/* Desktop Navigation */}
        <div className="hidden md:block">
          <PageHeader
            showBackButton={false}
            showEdit={can_edit}
            showDelete={can_edit}
            showShare={false}
            showFavorite={false}
            editHref={businessesEditPath(business.id)}
            onDelete={handleDelete}
          />
        </div>
        <div className="px-4">
          <div className="text-primary flex flex-wrap items-center gap-2 text-[13px] font-medium tracking-[0.13px]">
            <button
              onClick={() => window.history.back()}
              className="hover:underline"
            >
              <div className="flex items-center gap-1">
                <ArrowGo className="text-primary size-4 rotate-180 fill-current" />
                Negocios
              </div>
            </button>
            <span className="text-sm font-normal">•</span>
            <Link>{business?.business_name}</Link>
          </div>

          <div className="flex items-start justify-between gap-2">
            <h1 className="mt-[14px] text-[21px] font-semibold tracking-[0px] break-words sm:text-3xl">
              {business.business_name}
            </h1>
            <ContentActionsMenu
              contentType="Business"
              contentId={business.id}
              authorId={business.owner?.id ?? 0}
              authorName={business.owner?.name ?? business.business_name}
            />
          </div>
          <div className="text-muted-foreground flex flex-wrap items-center gap-[7px] text-sm">
            <div className="flex items-center">
              <StarFilled className="fill-star text-star mr-1 size-5" />
              <span className="text-chart-5 text-[13px] font-bold tracking-[0.20px]">
                {Number(business.rating ?? 0).toFixed(1)}
              </span>
              <span className="text-chart-5 bg-card ml-[3px] rounded-[8px] px-[7px] py-[2px] text-[11px] font-medium tracking-[0.26px]">
                {business.total_reviews} reseñas
              </span>
            </div>
            <span>•</span>
            <div className="text-muted-foreground flex items-center text-[13px] font-normal tracking-[0.20px]">
              <Pin className="fill-muted-foreground mr-1 size-4" />
              <span>{business.location}</span>
            </div>
          </div>
          <div className="mt-1 mb-2 flex flex-wrap items-center justify-between">
            <span className="bg-accent h-[17px] items-center rounded-[8px] px-[7px] py-[2px] text-[9.5px] font-normal tracking-[0.57px] text-white">
              {business.business_type.toUpperCase()}
            </span>
            <div className="bg-card flex flex-wrap items-center gap-[6px] rounded-[6px] px-[7px] py-[2px]">
              <div
                className={cn(
                  "h-[14px] w-[14px] rounded-full",
                  openNow ? "bg-active" : "bg-destructive",
                )}
              />

              <span className="ml-[3px] text-[12px] font-medium tracking-[0.24px]">
                {openNow ? "Abierto" : "Cerrado"}
              </span>
            </div>
          </div>
          <div className="mb-2 flex flex-wrap items-center gap-[14px]">
            <div className="flex items-center gap-[4px]">
              <AuthButton
                variant="ghost"
                size="icon"
                className="h-[14px] w-[16px]"
                onClick={() => void handleFavorite()}
              >
                {actualFavorited ? (
                  <HeartFilled className="text-destructive size-4.5 w-auto fill-current" />
                ) : (
                  <Heart className="size-4.5 w-auto" />
                )}
                <span className="sr-only">
                  {actualFavorited
                    ? "Quitar de favoritos"
                    : "Agregar a favoritos"}
                </span>
              </AuthButton>
              <span className="text-muted-foreground text-[13px] tracking-[0.20px]">
                {actualFavoritesCount}
              </span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-[20px] w-[20px]"
              onClick={() => void handleShare()}
            >
              <Share className="size-5" />
              <span className="sr-only">Compartir</span>
            </Button>
          </div>
        </div>
        {/* Hero Section */}
        <div className="grid lg:grid-cols-2">
          {/* Images */}
          <div className="space-y-4">
            <div className="bg-images relative h-[231px] overflow-hidden">
              {(business.images?.length ?? 0) > 0 ? (
                <>
                  <img
                    src={business.images?.[currentImageIndex]?.url ?? ""}
                    alt={business.business_name}
                    className="h-full w-full object-cover"
                  />
                  {business.images && business.images.length > 1 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="absolute top-1/2 left-2 -translate-y-1/2 transform bg-white/80"
                        onClick={prevImage}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="absolute top-1/2 right-2 -translate-y-1/2 transform bg-white/80"
                        onClick={nextImage}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 transform rounded bg-black/50 px-2 py-1 text-xs text-white">
                        {currentImageIndex + 1} / {business.images?.length || 0}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="text-ring flex h-[231px] items-center justify-center text-[18px] font-medium tracking-[0px]">
                  {/* IMAGENES EL ELLA */}
                  Sin imágenes disponibles
                </div>
              )}
            </div>

            {/* Image Thumbnails */}
            {business.images && business.images.length > 1 && (
              <div className="flex space-x-2 overflow-x-auto px-[20px]">
                {business.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border-2 ${
                      currentImageIndex === index
                        ? "border-primary"
                        : "border-transparent"
                    }`}
                  >
                    <img
                      src={image.url}
                      alt={business.business_name}
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Business Info */}
          <div className="space-y-4 px-[27px] py-[20px]">
            {/* Basic Info */}
            <span className="text-chart-5 text-[14px] font-semibold tracking-[0.14px]">
              Acerca del negocio
            </span>
            <div className="text-chart-5 text-[13px] font-normal tracking-[0.2px]">
              <span className="break-words whitespace-pre-wrap">
                {business.description}
              </span>
            </div>

            <div className="space-y-0.5">
              <div className="text-muted-foreground flex items-center gap-[10px] text-[13px] font-normal tracking-[0.20px]">
                <Pin className="text-muted-foreground h-4 w-4 fill-current" />
                <span>
                  {business.address}, {business.neighborhood?.name}
                </span>
              </div>
              {business.phone && (
                <div className="text-muted-foreground flex items-center gap-[10px] text-[13px] font-normal tracking-[0.20px]">
                  <Cel className="text-muted-foreground h-4 w-4 fill-current" />
                  <a href={`tel:${business.phone}`} className="hover:underline">
                    {business.phone}
                  </a>
                </div>
              )}
              {business.website && (
                <div className="flex items-center gap-[10px] text-[12px] font-normal tracking-[0.18px]">
                  <Www className="text-muted-foreground h-4 w-4 fill-current" />
                  <a
                    href={
                      business.website.startsWith("http")
                        ? business.website
                        : `https://${business.website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-chart-3 transition-colors hover:underline"
                  >
                    Sitio web
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Service Options */}

          <div className="border-chart-4 w-full border-t-[1px] p-[20px]">
            <div className="space-y-[10px]">
              {business.has_delivery && (
                <div className="flex items-center gap-[9px]">
                  <Truck className="fill-primary bg-card h-[32px] w-[32px] rounded-full p-[4px]" />
                  <span className="text-chart-5 text-[14px] font-semibold tracking-[0.14px]">
                    Delivery disponible
                  </span>
                </div>
              )}
              {business.has_pickup && (
                <div className="flex items-center gap-[9px]">
                  <Market className="text-primary fill-primary bg-card h-[32px] w-[32px] rounded-full p-[4px]" />
                  <span className="text-chart-5 text-[14px] font-semibold tracking-[0.14px]">
                    Recogida en tienda
                  </span>
                </div>
              )}
              {business.delivery_zones.length > 0 && (
                <div className="mt-3">
                  <div className="mb-1 text-sm font-medium">
                    Zonas de delivery:
                  </div>
                  <div className="text-muted-foreground text-sm">
                    {business.delivery_zones.join(", ")}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Owner Info */}
          <div className="border-chart-4 w-full border-t-[1px] px-[12px] py-[20px]">
            <span className="text-chart-5 px-[12px] text-[14px] font-semibold tracking-[0.14px]">
              Propietario
            </span>
            <div className="flex min-w-0 items-start gap-3 px-[12px]">
              <Avatar className="bg-card h-[60px] w-[60px]">
                <AvatarImage src={business.owner?.avatar_url} />
                <AvatarFallback className="bg-card">
                  <UserFilled className="fill-chart-2 size-11" />
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col justify-start">
                <Link
                  href={userPath(business.owner?.id)}
                  className="text-chart-5 flex flex-wrap items-center gap-1 text-[14px] font-semibold tracking-[0px]"
                >
                  {business.owner?.name ?? ""}
                  {business.owner?.verified && (
                    <Check className="fill-primary mb-0.5 size-4.5 text-white" />
                  )}
                </Link>
                <Link
                  href={userPath(business.owner?.id)}
                  className="text-primary text-[12px] font-medium tracking-[0.18px]"
                >
                  Ver Perfil
                </Link>
              </div>
            </div>
            <div className="flex w-full justify-between gap-[12px] py-[12px]">
              {business.phone && (
                <Button
                  variant="outline"
                  className="text-primary bg-card h-[49px] w-[49%] rounded-[14px] border-0 text-[15px] font-medium tracking-[0.45px]"
                  asChild
                >
                  <a href={`tel:${business.phone}`}>
                    <Cel className="mr-2 h-4 w-4 fill-current" />
                    Llamar
                  </a>
                </Button>
              )}
              <AuthButton
                className="h-[49px] w-[49%] rounded-[14px] border-0 text-[15px] font-medium tracking-[0.45px]"
                onClick={handleContact}
                authAction="register"
              >
                <Comment className="mr-2 h-4 w-4 fill-white" />
                Mensaje
              </AuthButton>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <Tabs
          defaultValue={products.length > 0 ? "products" : "hours"}
          className="border-chart-4 w-full border-t-[1px] py-[26px]"
        >
          <div className="flex justify-center">
            <TabsList className="bg-foreground mb-[10px] inline-flex h-auto rounded-[15px] px-[10px] py-[10px] tracking-[0.26px]">
              {products.length > 0 && (
                <TabsTrigger
                  value="products"
                  className="flex items-center gap-2 text-[13px] font-normal tracking-[0.26px] text-white data-[state=active]:bg-white data-[state=active]:font-bold"
                >
                  Productos
                </TabsTrigger>
              )}
              <TabsTrigger
                value="reviews"
                className="flex items-center gap-2 text-[13px] font-normal tracking-[0.26px] text-white data-[state=active]:bg-white data-[state=active]:font-bold"
              >
                Reseñas
              </TabsTrigger>
              <TabsTrigger
                value="hours"
                className="flex items-center gap-2 text-[13px] font-normal tracking-[0.26px] text-white data-[state=active]:bg-white data-[state=active]:font-bold"
              >
                Horarios
              </TabsTrigger>
            </TabsList>
          </div>

          {products.length > 0 && (
            <TabsContent value="products" className="space-y-6 px-[12px]">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {products.map((p: Product) => (
                  <FeedItem
                    key={p.id}
                    variant="grid"
                    item={
                      {
                        type: "product",
                        id: p.id,
                        created_at: p.created_at ?? new Date().toISOString(),
                        name: p.name,
                        price: p.price,
                        condition: p.condition ?? "good",
                        business: {
                          id: business.id,
                          name: business.business_name,
                          verified: business.is_verified,
                        },
                        images: p.images?.map((img) => img.url),
                      } as FeedItemData
                    }
                  />
                ))}
              </div>

              {pagination && pagination.total_pages > 1 && (
                <PaginationControls
                  currentPage={pagination.current_page}
                  totalPages={pagination.total_pages}
                  disablePrev={pagination.current_page <= 1}
                  disableNext={
                    pagination.current_page >= pagination.total_pages
                  }
                  onPrev={() =>
                    router.get(
                      `/negocios/${business.id}`,
                      {
                        page: pagination.current_page - 1,
                        per_page: pagination.per_page,
                      },
                      { preserveState: true, preserveScroll: true },
                    )
                  }
                  onNext={() =>
                    router.get(
                      `/negocios/${business.id}`,
                      {
                        page: pagination.current_page + 1,
                        per_page: pagination.per_page,
                      },
                      { preserveState: true, preserveScroll: true },
                    )
                  }
                />
              )}
            </TabsContent>
          )}

          <TabsContent value="reviews" className="space-y-6">
            <div className="space-y-4">
              <Dialog
                open={isReviewModalOpen}
                onOpenChange={setIsReviewModalOpen}
              >
                <DialogContent
                  showWelcomeLogo={false}
                  className="mt-4 w-[360px] sm:w-[420px]"
                >
                  <form onSubmit={handleReview} className="">
                    <CardTitle className="text-chart-5 spacing-[27px] mt-[16px] mb-[12px] h-[25px] text-center text-[18px] font-semibold">
                      Calificación
                    </CardTitle>
                    <div>
                      <span className="text-chart-5 spacing-[27px] flex h-[36px] items-center justify-center text-center text-[21px] font-medium">
                        {business.business_name}
                        {business.is_verified && (
                          <Check className="fill-primary ml-[6px] size-4.5" />
                        )}
                      </span>
                      <div className="mt-[10px] flex items-center justify-center">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setRating(star)}
                            aria-label={`Calificación ${star} estrellas`}
                          >
                            <StarFilled
                              className={`size-9 ${
                                star <= rating ? "fill-star" : "fill-chart-2"
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mt-[20px] flex flex-col items-center gap-[8px]">
                      <span className="text-chart-5 spacing-[27px] mb-[8px] text-center text-[18px] font-semibold">
                        Reseña
                      </span>
                      <RichTextEditor
                        key={formResetKey}
                        value={comment}
                        onChange={setComment}
                        placeholder="Escribe tu experiencia..."
                        minHeight="140px"
                      />
                    </div>
                    <div className="mt-[28px] flex items-center justify-center">
                      <AuthButton
                        type="submit"
                        authAction="register"
                        disabled={rating < 1 || !comment || alreadyReviewed}
                        className="h-[49px] rounded-[14px] px-[24px] py-[12px] text-[15px] font-normal tracking-[0.45px]"
                      >
                        Publicar
                      </AuthButton>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Reviews List */}
            <div id="reviews-list" className="space-y-4">
              {reviewsState.map((review) => (
                <div
                  key={review.id}
                  className="bg-background border-chart-4 border-b"
                >
                  <div className="flex items-start gap-3 px-[12px]">
                    <Avatar className="bg-card h-[47px] w-[47px]">
                      <AvatarImage src={review.user.avatar_url} />
                      <AvatarFallback className="bg-card">
                        <UserFilled className="fill-chart-2 size-9" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex flex-col items-start">
                        <div className="flex items-center gap-[4px]">
                          <Link
                            href={userPath(review.user.id)}
                            className="text-chart-5 flex truncate text-[13px] font-medium tracking-[0.2px]"
                          >
                            {review.user.name}
                            {review.user.verified && (
                              <Check className="fill-primary ml-[6px] size-4" />
                            )}
                          </Link>
                          <span className="text-muted-foreground text-[13px] font-normal">
                            • {formatDate(review.created_at)}
                          </span>
                        </div>
                        <div className="flex items-center">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <StarFilled
                              key={i}
                              className={`size-4 ${
                                i < review.rating ? "fill-star" : "fill-chart-2"
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                      {editingReviewId === review.id ? (
                        <form
                          onSubmit={(e) => submitEditReview(e, review)}
                          className="mt-2 space-y-3"
                        >
                          <div>
                            <label className="mb-1 block text-xs font-medium">
                              Actualizar calificación
                            </label>
                            <div className="flex space-x-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                  key={star}
                                  type="button"
                                  className="p-0.5"
                                  onClick={() => setEditRating(star)}
                                  aria-label={`Calificación ${star} estrellas`}
                                >
                                  <Star
                                    className={`h-5 w-5 ${star <= editRating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground hover:text-yellow-400"}`}
                                  />
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium">
                              Actualizar reseña
                            </label>
                            <RichTextEditor
                              value={editComment}
                              onChange={setEditComment}
                              minHeight="140px"
                            />
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="submit"
                              size="sm"
                              disabled={editRating < 1 || !editComment}
                            >
                              Guardar
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={cancelEditReview}
                            >
                              Cancelar
                            </Button>
                          </div>
                        </form>
                      ) : (
                        <>
                          <div
                            className="overflow-x-hidden text-sm break-words"
                            dangerouslySetInnerHTML={{ __html: review.comment }}
                          />
                          <div className="mt-2 flex basis-full flex-wrap items-center justify-end gap-2">
                            {user && review.user.id === user.id && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="text-xs"
                                onClick={() => beginEditReview(review)}
                              >
                                <Pencil className="mr-1 h-3.5 w-3.5" />
                              </Button>
                            )}
                            {user &&
                              (review.user.id === user.id || isAdmin) && (
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  className="text-xs"
                                  onClick={() => deleteReview(review)}
                                >
                                  <Trash className="mr-1 h-3.5 w-3.5" />
                                </Button>
                              )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-[12px]">
              {reviews_pagination && reviews_pagination.total_pages > 1 && (
                <PaginationControls
                  currentPage={reviews_pagination.current_page}
                  totalPages={reviews_pagination.total_pages}
                  disablePrev={reviews_pagination.current_page <= 1}
                  disableNext={
                    reviews_pagination.current_page >=
                    reviews_pagination.total_pages
                  }
                  onPrev={() =>
                    router.get(
                      `/negocios/${business.id}`,
                      {
                        reviews_page: reviews_pagination.current_page - 1,
                        reviews_per_page: reviews_pagination.per_page,
                      },
                      { preserveState: true, preserveScroll: true },
                    )
                  }
                  onNext={() =>
                    router.get(
                      `/negocios/${business.id}`,
                      {
                        reviews_page: reviews_pagination.current_page + 1,
                        reviews_per_page: reviews_pagination.per_page,
                      },
                      { preserveState: true, preserveScroll: true },
                    )
                  }
                />
              )}
              {renderReviewCTA()}
            </div>
          </TabsContent>

          <TabsContent value="hours" className="space-y-6 px-[12px]">
            <Card className="gap-0 rounded-[14px] border-0 px-[20px] pt-[8px] pb-[40px] shadow-[0,0,12px,FFFFFFF7]">
              <CardHeader className="flex flex-col items-center justify-center gap-0 p-[12px]">
                <CardTitle className="text-muted-foreground flex items-center text-[14px] font-medium tracking-[0.28px]">
                  <span className="mr-2 h-[44px] w-[44px] items-center justify-center rounded-full">
                    <img src={EmoClock} className="" />
                  </span>
                  Horario de atención
                </CardTitle>
                <div className="bg-card flex flex-wrap items-center gap-[6px] rounded-[6px] px-[7px]">
                  <div
                    className={cn(
                      "h-[14px] w-[14px] rounded-full",
                      openNow ? "bg-active" : "bg-destructive",
                    )}
                  />
                  <span className="ml-[3px] text-[12px] font-medium tracking-[0.24px]">
                    {openNow ? "Abierto" : "Cerrado"}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="mt-0">
                <div>
                  {business.hours && Object.keys(business.hours).length > 0 ? (
                    Object.entries(business.hours).map(([day, hours]) => {
                      const dayNames = {
                        monday: "Lunes",
                        tuesday: "Martes",
                        wednesday: "Miércoles",
                        thursday: "Jueves",
                        friday: "Viernes",
                        saturday: "Sábado",
                        sunday: "Domingo",
                      }

                      const isToday = day === getCurrentDay()

                      return (
                        <div
                          key={day}
                          className={`text-chart-5 border-chart-4 flex h-[32px] items-center justify-between border-b-[2px] px-[12px] text-[13px] tracking-[0.2px] ${isToday ? "bg-modal font-semibold" : ""}`}
                        >
                          <span className="flex items-center">
                            {isToday && (
                              <Today
                                className="mr-1 size-5 [&>*]:!fill-[#222222]"
                                style={{ color: "#222222", fill: "#222222" }}
                              />
                            )}
                            {dayNames[day as keyof typeof dayNames]}
                          </span>
                          <span className="">
                            {hours.closed
                              ? "Cerrado"
                              : `${hours.open} - ${hours.close}`}
                          </span>
                        </div>
                      )
                    })
                  ) : (
                    <p className="text-muted-foreground py-4 text-center">
                      Horarios no disponibles
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  )
}
